import { useUpdateComponent } from '@/shared/api';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Form } from 'antd';
import { highlight, languages } from 'prismjs';
import CodeEditor from 'react-simple-code-editor';
import 'prismjs/components/prism-markup.min.js';
import 'prismjs/themes/prism.css';
import styled from 'styled-components';
import { useProcContext } from '@/entities/proc';
import { useNotificationContext } from '@/features/notifications';
import { captureError } from '@/shared/utils/error-reporting';

export const CodeEditForm = forwardRef((_, ref) => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotificationContext();
  const editorRef = useRef<any>(null);
  const { selectedComponent, selectedStep, proc, setSelectedComponent } =
    useProcContext();
  const [form] = Form.useForm();

  const { mutate: updateComponent, isLoading: updateComponentLoading } =
    useUpdateComponent();

  const [code, setCode] = useState(selectedComponent?.value);

  useImperativeHandle(
    ref,
    () => ({
      submit: onSubmit,
      isLoading: updateComponentLoading,
    }),
    [updateComponentLoading, selectedComponent?.id, selectedStep?.id],
  );

  const onSubmit = async () => {
    const value = form.getFieldValue('code');

    if (!proc || !selectedStep || !selectedComponent || !value) {
      return;
    }

    try {
      await form.validateFields();
      updateComponent(
        {
          pathParams: { procId: proc.id, stepId: selectedStep?.id! },
          body: {
            id: selectedComponent.id,
            component: selectedComponent.component,
            value,
            componentName: selectedComponent.componentName,
          },
        },
        {
          onSuccess: () => {
            setSelectedComponent(undefined);
            queryClient.invalidateQueries({
              queryKey: ['procs', proc?.id],
            });
          },
          onError: (error: any) => {
            showNotification({
              type: 'error',
              message: 'Update error',
              description: error.stack.message,
            });
            captureError(error, {
              module: 'code_edit_form_on_error_update_component',
              action: 'update_component',
            });
          },
        },
      );
    } catch (error) {
      captureError(error, {
        module: 'code_edit_form_on_error_update_component',
        action: 'update_component',
      });
    }
  };

  useEffect(() => {
    editorRef.current?.focus();
  }, []);

  useEffect(() => {
    form.setFieldsValue({ code: selectedComponent?.value });
  }, [selectedComponent]);

  return (
    <Form
      data-testid="code-edit-form"
      form={form}
      name="codeEditForm"
      initialValues={{
        remember: true,
        code: selectedComponent?.value,
      }}
      onFinish={onSubmit}
      autoComplete="off"
      layout="vertical"
      style={{ width: '100%' }}
    >
      <Form.Item
        label="Field value"
        name="code"
        style={{ width: '100%' }}
        rules={[
          {
            required: true,
            message: 'Field value is required',
          },
        ]}
      >
        <CodeEditorWrapper>
          <CodeEditor
            value={code}
            onValueChange={(code) => setCode(code)}
            highlight={(code) => highlight(code, languages.html, 'html')}
            padding={10}
          />
        </CodeEditorWrapper>
      </Form.Item>
    </Form>
  );
});

const CodeEditorWrapper = styled.div`
  height: 70vh;
  overflow-y: auto;
  border: 1px solid gray;
`;
